import React, { useEffect, useState } from "react";
import {
  reduxForm,
  FormSection,
  getFormValues,
  getFormSyncErrors
} from "redux-form";
import AddressSellCheckout from "@components/forms/AddressSellCheckout";
import SellContactInformation from "./SellContactInformation";
import SecurityImages from "./SecurityImages";
import SellCardsDetails from "./SellCardsDetails";
import SellOrderSummary from "./SellOrderSummary";
import { completeSellOrder } from "@actions/sellOrders";
import { defaultAction } from "@actions/defaultAction";
import { postPrimaryAddress } from "@actions/customerInfo";
import { DISPLAY_MAIN_PAYMENT_ERROR } from "@constants/actionTypes";
import loadable from "@loadable/component";
const SellVerificationCC = loadable(() =>
  import(
    /* webpackChunkName: "SellVerificationCC" */ "./forms/SellVerificationCC"
  )
);
import MainContentSellContainer from "./MainContentSellContainer";
import SellFormDesktopBottomButton from "./SellFormDesktopBottomButton";
import { verifyCC } from "@utils/validators/verifyCC";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

const TradeOptionSellWorkflow = ({
  handleSubmit,
  submitFailed,
  dispatch,
  valid,
  change
}) => {
  const history = useHistory();
  const regularReduxDispatch = useDispatch();
  const phone = useSelector(state => state.customerInfo.phone);
  const missingInfo = useSelector(state => state.customerInfo.missingInfo);
  const mainPaymentError = useSelector(
    state => state.customerInfo.mainPaymentError
  );
  const customerGetStatus = useSelector(
    state => state.customerInfo.customerGetStatus
  );
  const tradeOptionSell = useSelector(state =>
    getFormValues("tradeOptionSell")(state)
  );
  const cardKnoxState = useSelector(state => state.cardKnox);
  const tradeOptionSellErrors = useSelector(state =>
    getFormSyncErrors("tradeOptionSell")(state)
  );
  const payoutMethodTradeID = useSelector(
    state => state.cartSell.payoutMethodTradeID
  );
  const offersTrade = useSelector(state => state.offers.trade);

  const [displayPrimary, setDisplayPrimary] = useState(null);
  const [phoneCompleted, changePhoneCompleted] = useState(!!phone);
  const [openPrimaryForm, changeOpenPrimaryForm] = useState(false);
  const [openVerificationForm, changeOpenVerificationForm] = useState(false);
  const [openToEdit, changeOpenToEdit] = useState(false);

  useEffect(() => {
    if (customerGetStatus === "success") {
      if (!missingInfo) {
        setDisplayPrimary(false);
      } else {
        setDisplayPrimary(true);
      }
    }
  }, [customerGetStatus]);

  useEffect(() => {
    if (!missingInfo) {
      changeOpenToEdit(true);
    }
  }, [missingInfo]);

  useEffect(() => {
    if (submitFailed && !mainPaymentError) {
      window.scrollTo(0, 0);
      regularReduxDispatch(
        defaultAction(
          DISPLAY_MAIN_PAYMENT_ERROR,
          "Please fix the errors and try again"
        )
      );
    }
  }, [submitFailed]);

  useEffect(() => {
    if (phoneCompleted) {
      if (missingInfo) {
        changeOpenPrimaryForm(true);
      }
    }
  }, [phoneCompleted]);

  useEffect(() => {
    if (openPrimaryForm) {
      if (openVerificationForm && missingInfo) {
        changeOpenVerificationForm(false);
      }
    }

    if (!openPrimaryForm) {
      if (!missingInfo) {
        changeOpenVerificationForm(true);
      }
    }
  }, [openPrimaryForm]);

  useEffect(() => {
    if (openVerificationForm) {
      if (openPrimaryForm) {
        changeOpenPrimaryForm(false);
      }
    }
  }, [openVerificationForm]);

  const primaryContinueAction = async () => {
    const response = await regularReduxDispatch(
      postPrimaryAddress(tradeOptionSell.primaryResidence)
    );
    return response;
  };

  const img = offersTrade.find(offer => offer.id === payoutMethodTradeID).logo;
  const submitTradeOptionOrder = handleSubmit(() => {
    regularReduxDispatch(completeSellOrder(history, "tradeOptionSell"));
  });

  return (
    <form onSubmit={submitTradeOptionOrder}>
      {tradeOptionSell && Object.keys(tradeOptionSell).length > 0 && (
        <MainContentSellContainer>
          <div>
            <SellCardsDetails />
            <SellContactInformation
              errors={tradeOptionSellErrors}
              changePhoneCompleted={changePhoneCompleted}
              phoneValue={tradeOptionSell?.newPhone}
              primaryResidence={
                displayPrimary ? null : tradeOptionSell.primaryResidence
              }
            />
            {displayPrimary && (
              <FormSection name="primaryResidence">
                <AddressSellCheckout
                  heading="Primary Residence"
                  phoneCompleted={phoneCompleted}
                  errors={tradeOptionSellErrors.primaryResidence}
                  addressLineTwo={tradeOptionSell?.primaryResidence?.street2}
                  formContent={tradeOptionSell?.primaryResidence}
                  openForm={openPrimaryForm}
                  changeOpenForm={changeOpenPrimaryForm}
                  flowNumber={"2"}
                  openToEdit={openToEdit}
                  continueAction={primaryContinueAction}
                  displayPrimary={displayPrimary}
                />
              </FormSection>
            )}
            <FormSection name="verification">
              <SellVerificationCC
                openForm={openVerificationForm}
                heading={"Identity Verification"}
                errors={tradeOptionSellErrors.verification}
                formContent={tradeOptionSell?.verification}
                openToEdit={openToEdit}
                missingInfo={missingInfo}
                changeOpenForm={changeOpenVerificationForm}
                flowNumber={displayPrimary ? "3" : "2"}
                checkBoxText={
                  "The address associated with this credit card is the same as my primary address"
                }
                checkBoxName={"sameAsPrimary"}
                addressLineTwo={tradeOptionSell?.primaryResidence?.street2}
                changeReduxField={change}
              />
            </FormSection>
            <SellFormDesktopBottomButton
              reduxFormDispatch={dispatch}
              valid={valid}
              img={img}
              missingInfo={
                missingInfo ||
                !verifyCC(tradeOptionSell?.verification, cardKnoxState)
              }
            />
          </div>
          <div>
            <SellOrderSummary
              reduxFormDispatch={dispatch}
              valid={valid}
              img={img}
              missingInfo={
                missingInfo ||
                !verifyCC(tradeOptionSell?.verification, cardKnoxState)
              }
            />
            <SecurityImages />
          </div>
        </MainContentSellContainer>
      )}
    </form>
  );
};

export default reduxForm({
  form: "tradeOptionSell"
})(TradeOptionSellWorkflow);
